import type { AnalyticsEventName, AnalyticsEventPayload } from "./events";

export { AnalyticsEvents } from "./events";
export type { AnalyticsEventName, AnalyticsEventPayload } from "./events";

type TrackerConfig = {
  enabled?: boolean | string;
  src?: string;
  websiteId?: string;
  domains?: string;
};

const TRACKER_ID = "axqora-umami-tracker";

let isAnalyticsEnabled = false;

function normalizeEnabled(value: boolean | string | undefined): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return false;

  return ["1", "true", "yes", "on"].includes(value.toLowerCase());
}

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function isEmbedView(): boolean {
  if (typeof window === "undefined") return false;

  try {
    return new URLSearchParams(window.location.search).get("embed") === "1";
  } catch {
    return false;
  }
}

function canUseAnalytics(enabled = true): boolean {
  if (!enabled) return false;
  if (typeof window === "undefined") return false;
  if (isEmbedView()) return false;

  return !isLocalHost(window.location.hostname);
}

export function mountUmamiTracker(config: TrackerConfig): boolean {
  if (typeof document === "undefined") return false;

  const enabled = normalizeEnabled(config.enabled);
  isAnalyticsEnabled = enabled;

  if (!canUseAnalytics(enabled)) return false;
  if (!config.src || !config.websiteId) return false;
  if (document.getElementById(TRACKER_ID)) return true;

  const script = document.createElement("script");
  script.id = TRACKER_ID;
  script.defer = true;
  script.src = config.src;
  script.dataset.websiteId = config.websiteId;
  script.dataset.excludeSearch = "true";
  script.dataset.excludeHash = "true";

  if (config.domains) {
    script.dataset.domains = config.domains;
  }

  document.head.appendChild(script);
  return true;
}

function sanitizePayload(payload?: AnalyticsEventPayload): AnalyticsEventPayload | undefined {
  if (!payload) return undefined;

  const allowedKeys: Array<keyof AnalyticsEventPayload> = ["product", "page", "locale", "target", "from", "to"];
  const sanitized: AnalyticsEventPayload = {};

  for (const key of allowedKeys) {
    const value = payload[key];
    if (typeof value === "string" && value.length > 0) {
      sanitized[key] = value.slice(0, 100);
    }
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

export function trackEvent(name: AnalyticsEventName, payload?: AnalyticsEventPayload): void {
  if (!isAnalyticsEnabled || !canUseAnalytics()) return;

  const umami = (window as unknown as {
    umami?: {
      track?: (eventName: string, data?: Record<string, unknown>) => void;
    };
  }).umami;

  if (!umami?.track) return;

  umami.track(name, sanitizePayload(payload));
}
